import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Box,
  Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  getUserAsync,
  selectUsers,
  clearUser,
  updateUser,
  updateUserAsync
} from '../../redux/slices/usersSlice';
import useAsync from '../../hooks/useAsync';
import UserTypeSelect from '../../containers/UserTypeSelect';
import { User } from '../../resources/types/usersTypes';
import { InputChangeEvent } from '../../resources/utils';
import MaskedInput from '../../containers/MaskedInput';
import { formatDate } from '../../resources/functions';
import UserBalance from '../../containers/UserBalance';
import UserBonusBalance from '../../containers/UserBonusBalance';
import { RootState } from '../../redux/store';

const PREFIX = 'UsersDialogContent';

const classes = {
  content: `${PREFIX}-content`,
  spinner: `${PREFIX}-spinner`,
  inputGroup: `${PREFIX}-inputGroup`
};

const Root = styled(Box)({
  [`&.${classes.content}`]: {
    position: 'relative',
    padding: '20px'
  },
  [`& .${classes.spinner}`]: {
    position: 'absolute',
    left: 0,
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.10) 50%, rgba(255,255,255,0.05) 100%)',
    zIndex: 1
  },
  [`& .${classes.inputGroup}`]: {
    display: 'flex',
    '& > div + div': {
      marginLeft: 10
    }
  }
});

interface Props {
  userId: string;
  onSave?: () => void;
}

const UsersDialogContent = ({ userId, onSave }: Props) => {
  const { t } = useTranslation();
  const { userById } = useSelector(selectUsers);
  const dispatch = useDispatch();

  const isSuperAdmin = useSelector(
    (state: RootState) => state.auth.info?.type === 'superAdmin'
  );

  const getUser = useCallback(async () => {
    if (userId) {
      await dispatch(getUserAsync(userId));
    }
  }, [dispatch, userId]);

  const { execute, pending } = useAsync(getUser);

  useEffect(() => {
    execute();
    return () => {
      dispatch(clearUser());
    };
  }, [dispatch, execute]);

  const [birthday, setBirthday] = useState('');
  useEffect(() => {
    setBirthday(formatDate(userById.birthday));
  }, [userById.birthday]);

  const changeBirthday = (e: InputChangeEvent) => {
    setBirthday(e.target.value);
  };

  const handleChange = useCallback(
    (name: keyof User) => (e: InputChangeEvent) => {
      dispatch(updateUser({ [name]: e.target.value }));
    },
    [dispatch]
  );

  const handleSubmit = () => {
    if (userId) {
      const body = {
        ...userById,
        birthday: birthday
          ? moment(birthday, 'DD.MM.YYYY').format('YYYY-MM-DD')
          : ''
      };

      dispatch(
        updateUserAsync(userId, body, () => {
          if (onSave) {
            onSave();
          }
        })
      );
    }
  };

  return (
    <Root className={classes.content}>
      {pending && (
        <div className={classes.spinner}>
          <CircularProgress color='primary' />
        </div>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <TextField
            fullWidth
            margin='none'
            label={t('userModal.lastName')}
            value={userById.lastName || ''}
            onChange={handleChange('lastName')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TextField
            fullWidth
            margin='none'
            label={t('userModal.firstName')}
            value={userById.firstName || ''}
            onChange={handleChange('firstName')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <TextField
            fullWidth
            margin='none'
            label={t('userModal.middleName')}
            value={userById.middleName || ''}
            onChange={handleChange('middleName')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <UserTypeSelect 
            value={userById.type} 
            onChange={handleChange('type')} 
            margin='none'
            sx={{
              '& .MuiOutlinedInput-input': {
                paddingTop: '16.5px',
                paddingBottom: '16.5px'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MaskedInput
            fullWidth
            shrinkLabel
            label={t('userModal.birthDate')}
            margin='none'
            maskProps={{
              mask: '99.99.9999'
            }}
            placeholder={t('userModal.birthDatePlaceholder')}
            value={birthday}
            onChange={changeBirthday}
          />
        </Grid>
      </Grid>
      
      {userId && (
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2, 
            mb: 1,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <strong>{t('userModal.instructionLabel')}</strong> {t('userModal.balanceInstruction')}
        </Alert>
      )}
      
      {userId && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <UserBalance
              fullWidth
              balance={userById.balance}
              margin='none'
              userId={userId}
              onSave={onSave}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UserBonusBalance
              fullWidth
              bonusPoints={userById.bonusPointsBalance || 0}
              margin='none'
              userId={userId}
              onSave={onSave}
            />
          </Grid>
        </Grid>
      )}
      
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('userModal.email')}
            margin='none'
            value={userById.email || ''}
            onChange={handleChange('email')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            inputProps={{ readOnly: true }}
            label={t('userModal.phone')}
            margin='none'
            value={userById.phone || ''}
          />
        </Grid>
      </Grid>
      <Grid
        item
        marginLeft={'auto'}
        flexDirection={isSuperAdmin ? 'row' : 'row-reverse'}
        display={'flex'}
        marginTop={2}
      >
        <Grid marginX={'5px'}>
          <Button color='primary' variant='contained' onClick={handleSubmit}>
            {t('userModal.save')}
          </Button>
        </Grid>
      </Grid>
    </Root>
  );
};

export default UsersDialogContent;
